"use client";

import * as React from "react";
import { signOut, useSession } from "next-auth/react";
import { toast } from "sonner";
import {
  Activity,
  Bot,
  Boxes,
  Code2,
  CreditCard,
  GraduationCap,
  LayoutDashboard,
  Loader2,
  LogOut,
  Settings,
  ShieldCheck,
  Users,
  Wallet,
  Wifi,
  Landmark,
  Eye,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { pushView } from "@/stores/useAppStore";
import { faNum } from "@/lib/format";

import AdminStats from "./AdminStats";
import PendingPayments from "./PendingPayments";
import AllPayments from "./AllPayments";
import BankCardsManager from "./BankCardsManager";
import UsersTable from "./UsersTable";
import PlansManager from "./PlansManager";
import SubscriptionsManager from "./SubscriptionsManager";
import SettingsPanel from "./SettingsPanel";
import AiOrdersPanel from "./AiOrdersPanel";
import CoursesManager from "./CoursesManager";
import AiProductsManager from "./AiProductsManager";
import ProjectRequestsPanel from "./ProjectRequestsPanel";
import CommissionPanel from "./CommissionPanel";
import type { AdminStats as Stats } from "./types";

const TABS = [
  { value: "dashboard", label: "داشبورد", icon: LayoutDashboard },
  { value: "pending", label: "در انتظار تأیید", icon: Activity },
  { value: "payments", label: "پرداختها", icon: CreditCard },
  { value: "bankcards", label: "کارتهای بانکی", icon: Landmark },
  { value: "users", label: "کاربران", icon: Users },
  { value: "plans", label: "طرحها", icon: ShieldCheck },
  { value: "subscriptions", label: "اشتراک‌ها", icon: Wifi },
  { value: "aiorders", label: "سفارشهای AI", icon: Bot },
  { value: "courses", label: "دورهها", icon: GraduationCap },
  { value: "aiproducts", label: "محصولات AI", icon: Boxes },
  { value: "projects", label: "درخواست پروژه", icon: Code2 },
  { value: "commission", label: "تسویه کمیسیون", icon: Wallet },
  { value: "settings", label: "تنظیمات", icon: Settings },
] as const;

type TabValue = (typeof TABS)[number]["value"];

function AdminFooter() {
  return (
    <footer className="mt-auto border-t bg-card/40 px-4 py-4 text-center text-xs text-muted-foreground">
      آکادمی فَران — تمام حقوق محفوظ است.
    </footer>
  );
}

function TopBar({
  adminName,
  onSiteClick,
  onLogout,
}: {
  adminName: string;
  onSiteClick: () => void;
  onLogout: () => void;
}) {
  return (
    <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
            <ShieldCheck className="size-5" />
          </span>
          <div className="leading-tight">
            <div className="text-sm font-bold sm:text-base">
              آکادمی فَران — پنل مدیریت
            </div>
            <div className="text-[11px] text-muted-foreground sm:text-xs">
              {adminName ? `ادمین: ${adminName}` : "پنل ادمین"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onSiteClick}>
            <Eye className="size-4" />
            <span className="hidden sm:inline">مشاهده سایت</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={onLogout}
          >
            <LogOut className="size-4" />
            <span className="hidden sm:inline">خروج</span>
          </Button>
        </div>
      </div>
    </header>
  );
}

export default function AdminPanel() {
  const { data: session } = useSession();
  const adminName = session?.user?.name ?? "ادمین";
  const [tab, setTab] = React.useState<TabValue>("dashboard");
  const [stats, setStats] = React.useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = React.useState(true);
  const [refreshKey, setRefreshKey] = React.useState(0);

  const loadStats = React.useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await fetch("/api/admin/stats", { cache: "no-store" });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت آمار");
      }
      setStats(data as Stats);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت آمار";
      toast.error(msg);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadStats();
  }, [loadStats, refreshKey]);

  function handleRefresh() {
    setRefreshKey((k) => k + 1);
  }

  function goSite() {
    pushView("dashboard");
  }

  async function handleLogout() {
    try {
      await signOut({ redirect: false });
      toast.success("خروج با موفقیت انجام شد.");
      setTimeout(() => {
        window.location.assign("/");
      }, 300);
    } catch (e) {
      toast.error("خطا در خروج.");
    }
  }

  const pendingCount = stats?.counts.pending ?? 0;
  const pendingAiOrders = stats?.counts.pendingAiOrders ?? 0;
  const pendingWithdrawals = stats?.counts.pendingWithdrawals ?? 0;
  const projectRequests = stats?.counts.projectRequests ?? 0;

  const badgeForTab: Partial<Record<TabValue, number>> = {
    pending: pendingCount,
    aiorders: pendingAiOrders,
    commission: pendingWithdrawals,
    projects: projectRequests,
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopBar
        adminName={adminName}
        onSiteClick={goSite}
        onLogout={handleLogout}
      />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
          <div className="mb-6 overflow-x-auto scrollbar-thin">
            <TabsList className="h-auto flex-wrap gap-1 rounded-xl p-1">
              {TABS.map((t) => {
                const Icon = t.icon;
                const badge = badgeForTab[t.value] ?? 0;
                const loadingBadge =
                  statsLoading &&
                  (t.value === "pending" ||
                    t.value === "aiorders" ||
                    t.value === "commission" ||
                    t.value === "projects") &&
                  badge === 0;
                return (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className="relative gap-1.5 rounded-lg px-3 py-1.5"
                  >
                    <Icon className="size-4" />
                    <span className="text-xs sm:text-sm">{t.label}</span>
                    {badge > 0 && (
                      <Badge className="ml-1 bg-amber-500 text-[10px] text-white hover:bg-amber-500/90">
                        {faNum(badge)}
                      </Badge>
                    )}
                    {loadingBadge && (
                      <Loader2 className="ml-1 size-3 animate-spin text-muted-foreground" />
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <TabsContent value="dashboard" className="mt-0">
            {statsLoading || !stats ? (
              <DashboardSkeleton />
            ) : (
              <AdminStats stats={stats} />
            )}
          </TabsContent>

          <TabsContent value="pending" className="mt-0">
            <PendingPayments onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="payments" className="mt-0">
            <AllPayments onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="bankcards" className="mt-0">
            <BankCardsManager onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="users" className="mt-0">
            <UsersTable />
          </TabsContent>

          <TabsContent value="plans" className="mt-0">
            <PlansManager onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="subscriptions" className="mt-0">
            <SubscriptionsManager onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="aiorders" className="mt-0">
            <AiOrdersPanel onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="courses" className="mt-0">
            <CoursesManager onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="aiproducts" className="mt-0">
            <AiProductsManager onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="projects" className="mt-0">
            <ProjectRequestsPanel />
          </TabsContent>

          <TabsContent value="commission" className="mt-0">
            <CommissionPanel onRefresh={handleRefresh} />
          </TabsContent>

          <TabsContent value="settings" className="mt-0">
            <SettingsPanel />
          </TabsContent>
        </Tabs>
      </main>

      <AdminFooter />
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full rounded-2xl" />
        ))}
      </div>
      <Skeleton className="h-20 w-full rounded-2xl" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Skeleton className="h-72 w-full rounded-2xl" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    </div>
  );
}
